import { UserRole } from '../types/user.types';

export interface StepGuide {
  step: number;
  title: string;
  description: string;
  screenshot?: string;
  tip?: string;
}

export interface HelpTopic {
  id: string;
  title: string;
  category: string;
  description: string;
  content: string; // Markdown supported
  relatedTopics: string[];
  roles?: UserRole[]; // Show only to specific roles
  keywords: string[];
  videoUrl?: string;
  steps?: StepGuide[];
  contextPaths?: string[]; // URL paths where this help is relevant
  windowIds?: string[]; // PropCloud HQ window IDs where this help is relevant
}

export const helpCategories = [
  { id: 'getting-started', label: 'Getting Started', icon: '🚀' },
  { id: 'developments', label: 'Development Management', icon: '🏢' },
  { id: 'sales', label: 'Sales Tools', icon: '💼' },
  { id: 'reports', label: 'Reports', icon: '📊' },
  { id: 'crm', label: 'CRM & Leads', icon: '👥' },
  { id: 'users', label: 'User Management', icon: '👤' },
  { id: 'payments', label: 'Payments & Invoicing', icon: '💳' },
  { id: 'viewing', label: 'Viewing Management', icon: '👁️' },
  { id: 'settings', label: 'System Settings', icon: '⚙️' },
  { id: 'troubleshooting', label: 'Troubleshooting', icon: '🔧' }
];

export const helpTopics: HelpTopic[] = [
  // Getting Started
  {
    id: 'welcome',
    title: 'Welcome to PropCloudAI',
    category: 'getting-started',
    description: 'Get started with PropCloudAI property management system',
    content: `# Welcome to PropCloudAI

PropCloudAI is a comprehensive property management system designed to streamline your real estate operations.

## Key Features
- **Development Management**: Create and manage property developments
- **Sales Tools**: Handle pre-reservations, reservations, and deal capture
- **CRM System**: Manage leads and contacts
- **Reporting**: Generate comprehensive reports
- **User Management**: Control access and permissions

## Quick Start

1. Navigate using the top menu bar
2. Access PropCloud HQ for advanced features
3. Check your role-based permissions

## Need Help?

- Use the Help button in PropCloud HQ
- Contact support through the ticket system
- View video tutorials for complex workflows`,
    relatedTopics: ['navigation', 'user-roles', 'propcloud-hq-navigation'],
    keywords: ['start', 'begin', 'intro', 'welcome', 'overview'],
    contextPaths: ['/dashboard']
  },

  // Development Management
  {
    id: 'create-development',
    title: 'Creating a Development',
    category: 'developments',
    description: 'Learn how to create a new property development',
    content: `# Creating a Development

## Steps to Create a Development

### 1. Access Development Creation
- Go to **PropCloud HQ** → **Development** → **Create Development**
- Or navigate to **Developments** → **New Development**

### 2. Fill in Basic Information
- **Development Name**: Enter the project name
- **Location**: Use the address search to set location
- **Description**: Provide development details
- **Total Units**: Specify the number of units

### 3. Set Commission Structure
- **Commission Type**: Choose percentage or fixed amount
- **Commission Value**: Enter the commission rate
- **Referral Mandate**: Optionally add referral details

### 4. Add Units
After creating the development:
- Navigate to **Edit Units in Development**
- Add units individually or in bulk
- Set unit numbers, types, and prices

## Important Notes
- Commission structure affects all future sales
- Location is used for viewing management
- Units can be edited after creation`,
    relatedTopics: ['edit-development', 'manage-units', 'commission-setup'],
    keywords: ['create', 'new', 'development', 'property', 'project'],
    contextPaths: ['/developments/new'],
    windowIds: ['create-development'],
    steps: [
      {
        step: 1,
        title: 'Open Development Form',
        description: 'Navigate to Development → Create Development',
        tip: 'Ensure you have the necessary permissions'
      },
      {
        step: 2,
        title: 'Enter Basic Details',
        description: 'Fill in name, location, and description',
        tip: 'Use descriptive names for easy identification'
      },
      {
        step: 3,
        title: 'Configure Commission',
        description: 'Set commission type and value',
        tip: 'This affects all sales in this development'
      },
      {
        step: 4,
        title: 'Save Development',
        description: 'Click Save to create the development',
        tip: 'You can edit details later if needed'
      }
    ]
  },

  // Sales Tools - Deal Capture
  {
    id: 'capture-deal-workflow',
    title: 'Deal Capture Workflow',
    category: 'sales',
    description: 'Complete guide to capturing a deal from reservation to completion',
    content: `# Deal Capture Workflow

The deal capture process consists of **5 main tabs** that must be completed in sequence.

## 1. Reservation Tab
- **Upload Document**: Upload the signed reservation agreement
- **Signature Date**: Enter when the client signed
- **Status Update**: Unit moves to "Reserved" status
- **Timer**: 72-hour timer starts for OTP submission

## 2. OTP Tab (Offer to Purchase)
### Document Upload
- Upload the signed OTP document (max 20MB)
- PDF format recommended

### Contact Management
- Primary contact auto-loaded from reservation
- Add additional buyers if needed
- Search existing or create new contacts

### Purchaser Details
Choose purchaser type:
- **Personal**: Individual buyer
- **Company (PTY/LTD)**: Company purchase
- **Trust**: Trust purchase

### Important Dates
- **Purchaser Signature**: Required field
- **Seller Signature**: Optional but starts 30-day timer
- ⚠️ **Warning**: Seller signature requires password confirmation

### Deal Type
- **Cash Deal**: Full payment without financing
- **Finance Deal**: Requires bond application

## 3. Review OTP Tab (HQ Only)
- **Document Review**: 90% width viewer
- **Checklist**: Verify all requirements
- **Approval**: Approve or reject with password
- **Rejection**: Must provide detailed reason

## 4. Deposits Tab
### Finance Deals
- Track reservation deposits
- Monitor bond application progress
- Update payment statuses

### Cash Deals
- Track all cash payments
- Add payment dates and amounts
- System calculates outstanding balance

## 5. Bond Tab
- **Bond Originator**: Select PropInvest or other
- **Finance Amount**: Enter bond amount
- **Application Status**: Track approval progress
- **FICA Documents**: Upload required documents

## Status Progression
1. **Available** → Pre-Reserved (48hr)
2. **Pre-Reserved** → Reserved (72hr)
3. **Reserved** → OTP Stages
4. **OTP Stages** → Finance/Cash workflows
5. **Completed** → Transferred → Commissions

## Common Issues & Solutions

### Can't Edit Locked Section
- Click the edit button
- Enter your password to unlock
- Only certain roles can edit OTP details

### Timer Expired
- Contact administrator for extension
- Extensions limited to one per unit

### Status Not Updating
- Ensure all required fields are filled
- Check that documents are uploaded
- Verify dates are entered correctly`,
    relatedTopics: ['pre-reservation', 'unit-status', 'cancel-deal'],
    keywords: ['deal', 'capture', 'otp', 'reservation', 'workflow', 'process'],
    contextPaths: ['/capture-deal'],
    windowIds: ['capture-deal'],
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
      UserRole.INDEPENDENT_PARTNER_LVL3,
      UserRole.PARTNER_AGENT_LVL3
    ]
  },

  // Reports
  {
    id: 'sales-reports',
    title: 'Sales Reports',
    category: 'reports',
    description: 'Generate and understand sales reports',
    content: `# Sales Reports

Sales reports show unit status across developments for tracking deal progress.

## Report Types

### HQ Report
Shows all units across all developments with:
- Development name
- Unit details (number, type, size)
- Current status
- Consultant information
- Buyer details

### Consultant Report
Filtered to show only units for specific consultants

### Partner Report
Shows units for specific partner organizations

## Using Filters

### Development Filter
- **All Developments**: Complete overview
- **Specific Development**: Focus on one project

### Status Filter
Multi-select from categories:
- **Reserved**: Pre-Reserved, Reserved
- **In Progress**: All OTP stages
- **Completed**: 100% paid, transferred
- **Other**: Cancelled, declined

### Date Range
Filter by:
- Reservation date
- OTP date
- Completion date

## Exporting Reports

### PDF Export
- A4 landscape format
- Includes all visible columns
- Headers and footers with dates

### Excel Export
- Download as .xlsx file
- Filterable columns
- Pivot table ready

## Understanding Statuses
- **Available**: Unit ready for sale
- **Pre-Reserved**: 48-hour hold
- **Reserved**: 72-hour for OTP
- **OTP Stages**: Various approval steps
- **Completed**: Deal finalized`,
    relatedTopics: ['bonds-report', 'cash-deals-report', 'commission-reports'],
    keywords: ['sales', 'report', 'units', 'status', 'export', 'pdf'],
    contextPaths: ['/reports/sales'],
    windowIds: ['sales-reports']
  },

  // CRM & Leads
  {
    id: 'lead-management',
    title: 'Lead Management',
    category: 'crm',
    description: 'Managing and scoring leads in the CRM system',
    content: `# Lead Management

## Lead Sources
PropCloudAI captures leads from:
- **Facebook**: Direct integration
- **Website**: Form submissions
- **WhatsApp**: Chat interactions
- **SMS**: Text message inquiries
- **Manual**: Direct entry

## Lead Scoring
Leads are automatically scored based on:
- **Income**: 85% weight - Monthly household income
- **Timeline**: 8% weight - Buying timeframe
- **Deposit**: 5% weight - Available deposit amount
- **Property Ownership**: 2% weight - Current property status

### Score Ranges
- **85-100%**: Hot leads - immediate follow-up required
- **65-84%**: High quality leads - priority follow-up
- **40-64%**: Medium quality leads - regular nurturing
- **0-39%**: Low quality leads - periodic contact

## Bulk Assignment
Distribute leads efficiently:
1. Select multiple leads
2. Choose assignment strategy:
   - **Round Robin**: Equal distribution
   - **Performance Based**: Top performers first
   - **Manual**: Specific agent selection
3. Confirm assignment

## Lead Status Updates
- **New**: Uncontacted lead
- **Contacted**: Initial contact made
- **Qualified**: Meets criteria
- **Opportunity**: Active opportunity
- **Converted**: Became client
- **Lost**: No longer interested`,
    relatedTopics: ['contact-management', 'facebook-leads', 'lead-scoring-config'],
    keywords: ['leads', 'crm', 'contacts', 'scoring', 'assignment'],
    contextPaths: ['/crm'],
    windowIds: ['lead-management']
  },

  // Viewing Management
  {
    id: 'viewing-management',
    title: 'Viewing Management System',
    category: 'viewing',
    description: 'Book and manage property viewings',
    content: `# Viewing Management

## Booking a Viewing

### 1. Select Development
Choose the development for viewing

### 2. Check Availability
View available time slots based on:
- Viewing agent schedule
- Development availability
- Existing bookings

### 3. Enter Contact Details
- Contact name and phone
- Email address
- Special requirements

### 4. Confirm Booking
- Select date and time
- Assign viewing agent
- Send confirmation

## Viewing Agent Setup

### Assign to Development
1. Go to Development Details
2. Click "Manage Viewing Agents"
3. Select agent and assistants
4. Set payment rates

### Payment Structure
- **Per Viewing**: Fixed rate per viewing
- **Per Successful**: Bonus for conversion

## Post-Viewing Process

### Automatic Webhooks
- **Immediate**: Booking confirmation
- **24 Hours Later**: Report request

### Report Submission
Viewing agents receive tokenized link to:
- Submit viewing outcome
- Add notes and feedback
- No login required

### Report Status
- **Pending**: Awaiting submission
- **Submitted**: Report received
- **Overdue**: Past deadline

## Configuring Report Delays
1. Go to **System Settings**
2. Find **Viewing Report Delay**
3. Set hours (1-168)
4. Save changes

💡 **Tip**: Set to 1 hour for testing, 24 hours for production`,
    relatedTopics: ['book-viewing', 'viewing-calendar', 'viewing-reports'],
    keywords: ['viewing', 'book', 'agent', 'property', 'showing'],
    contextPaths: ['/viewing'],
    windowIds: ['book-viewing']
  },

  // System Settings
  {
    id: 'commission-settings',
    title: 'Commission & VAT Settings',
    category: 'settings',
    description: 'Configure system-wide commission and VAT settings',
    content: `# Commission & VAT Settings

## Understanding Commission Structure

### Base Commission (HQ Level)
Set at development creation:
- **Fixed Amount**: Specific Rand value
- **Percentage**: % of selling price

### VAT Handling
**Always remove VAT first**:
- De-vatted = Commission ÷ 1.15
- This is what HQ earns as base

### Referral Mandate
If applicable:
- Gets **5% of de-vatted commission**
- HQ keeps 95%
- Not 5% of selling price!

### Sales Person Commission
Percentage of **FULL de-vatted commission**:
- **Level 1**: 30% of de-vatted
- **Level 2**: 50% of de-vatted
- **Level 3**: 70% of de-vatted
- **Partner Agent**: % of partner's 70%

## Example Calculation
\`\`\`
Sale Price: R1,000,000
Commission: 5% = R50,000
De-vatted: R50,000 ÷ 1.15 = R43,478

With Referral:
- Referral: R43,478 × 5% = R2,174
- HQ after referral: R41,304

Sales Person (Level 1):
- Consultant: R43,478 × 30% = R13,043
- HQ Final: R43,478 - R2,174 - R13,043 = R28,261
\`\`\`

## Changing Settings

### Access Requirements
- Super User role required
- Password confirmation needed

### Impact Warning
⚠️ Changes affect **future calculations only**
- Existing deals unchanged
- New deals use new rates

### Setting Values
1. Navigate to **System Settings**
2. Enter new percentages
3. Confirm with password
4. Save changes`,
    relatedTopics: ['development-commission', 'payment-calculations'],
    keywords: ['commission', 'vat', 'settings', 'percentage', 'calculation'],
    contextPaths: ['/commission-settings'],
    roles: [UserRole.PROPCLOUDAI_SUPER_USER]
  },

  // User Management
  {
    id: 'user-management',
    title: 'User Management',
    category: 'users',
    description: 'Create and manage users in the system',
    content: `# User Management

## Creating Users

### Who Can Create Users?
Only users with these roles can create new users:
- PropCloudAI Super User
- PropCloudAI User Management
- HQ Manager

### Steps to Create a User
1. Navigate to **Users** → **Create User**
2. Fill in user details:
   - First Name & Last Name
   - Email Address (must be unique)
   - Mobile Number
   - Select Role (determines permissions)
3. Click **Create User**
4. System sends onboarding email

### User Roles Explained

#### System Level
- **Super User**: Full system access
- **User Management**: Manage users only
- **Activity Viewer**: View logs only
- **System Configuration**: System settings

#### HQ Level
- **HQ Manager**: Manage HQ operations
- **HQ Admin**: Administrative tasks
- **HQ Bonds**: Bond origination
- **HQ Consultant Level 1**: 30% commission
- **HQ Independent Level 2**: 50% commission

#### Partner Level
- **Independent Partner Level 3**: 70% commission
- **Partner Agent**: Works under partner

#### Special Roles
- **Viewing Agent**: Property viewings only

## Managing Existing Users

### Edit User
1. Go to **Users** list
2. Click edit icon
3. Update details
4. Save changes

### Disable User
- Temporarily blocks access
- User data preserved
- Can be re-enabled

### Delete User
- Permanent removal
- Requires Super User role
- Archives user data

## Onboarding Process
New users must complete:
1. Email verification
2. Password creation
3. Profile photo upload
4. Personal details
5. Partner info (if applicable)`,
    relatedTopics: ['user-roles', 'permissions', 'onboarding'],
    keywords: ['users', 'create', 'manage', 'roles', 'permissions'],
    contextPaths: ['/users'],
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.PROPCLOUDAI_USER_MANAGEMENT,
      UserRole.HQ_MANAGER
    ]
  },

  // Pre-Reservation Process
  {
    id: 'pre-reservation',
    title: 'Pre-Reservation Process',
    category: 'sales',
    description: 'How to pre-reserve units for clients',
    content: `# Pre-Reservation Process

## What is Pre-Reservation?

A **48-hour hold** on a unit while client decides. No documentation required.

## Steps to Pre-Reserve

### 1. Navigate to Pre-Reserve

- Go to **Sales Tools** → **Pre-Reserve**
- Or use **PropCloud HQ** menu

### 2. Select Development

- Choose the development
- View available units

### 3. Select Unit

- Click on available unit
- Check price and details

### 4. Enter Contact Details

- Select existing contact
- Or create new contact
- Verify phone and email

### 5. Confirm Pre-Reservation

- Review details
- Click **Pre-Reserve**
- Unit status changes to "Pre-Reserved"

## Timer System

- **48-hour countdown** starts immediately
- Visible timer shows remaining time
- Auto-expires if not converted

## Converting to Reservation

Before 48 hours expires:

1. Navigate to unit
2. Click **Convert to Reservation**
3. Upload reservation document
4. Enter signature date

## If Timer Expires

- Unit automatically becomes Available
- Must start process again
- No extensions for pre-reservations

## Best Practices

- Pre-reserve only when client is serious
- Follow up within 24 hours
- Have reservation docs ready
- Convert early to avoid expiry`,
    relatedTopics: ['capture-deal-workflow', 'unit-status'],
    keywords: ['pre-reserve', 'hold', '48 hours', 'timer'],
    contextPaths: ['/pre-reserve'],
    windowIds: ['pre-reserve-search']
  },

  // Unit Status Guide
  {
    id: 'unit-status-guide',
    title: 'Understanding Unit Statuses',
    category: 'sales',
    description: 'Complete guide to all unit status levels',
    content: `# Unit Status Guide

## Status Progression Flow

### 1. Available
- Unit ready for sale
- No holds or reservations
- Can be pre-reserved

### 2. Pre-Reserved
- 48-hour hold
- No documentation
- Auto-expires

### 3. Reserved
- 72-hour for OTP
- Reservation document uploaded
- Timer running

### 4. OTP Stages

#### OTP Uploaded - HQ Pending
- OTP document uploaded
- Awaiting HQ review

#### OTP Awaiting Seller
- Waiting for seller signature
- 30-day timer if signed

#### OTP Await Financials
- Finance deal in progress
- Awaiting bond application

#### OTP Cash Deal
- Cash payment in progress
- Tracking payments

#### OTP at Bank
- Bond application submitted
- Awaiting bank response

#### OTP - AIP
- Approval in Principle received
- Awaiting final grant

#### OTP - Final Grant
- Bond approved
- Ready for transfer

### 5. Finance Statuses

#### Finance Declined
- Bond application rejected
- Deal may be cancelled

#### Finance Accepted
- Client accepted bond terms
- Moving to completion

### 6. Completion Stages

#### Completed - Awaiting Deposit
- Deal done, deposit pending

#### Completed 100%
- All payments received
- Ready for transfer

#### Transferred
- Property transferred
- Registration complete

#### Commissions Received
- All commissions paid
- Deal fully closed

## Status Timers

| Status | Timer | Action on Expiry |
|--------|-------|------------------|
| Pre-Reserved | 48 hours | Returns to Available |
| Reserved | 72 hours | Returns to Available |
| OTP Awaiting Seller | 30 days | Manual intervention |

## Changing Status
Status changes automatically based on:
- Document uploads
- Payment records
- Time expiry
- Admin actions`,
    relatedTopics: ['capture-deal-workflow', 'pre-reservation'],
    keywords: ['status', 'unit', 'otp', 'stages', 'progression'],
    contextPaths: ['*']
  },

  // Support Tickets
  {
    id: 'support-tickets',
    title: 'Support Ticket System',
    category: 'troubleshooting',
    description: 'How to create and manage support tickets',
    content: `# Support Ticket System

## Creating a Ticket

### Access Support
1. Click the red **Get Support** button in the top navigation
2. Or go to **My Tickets**

### Submit Ticket
1. Select ticket type:
   - Admin Support
   - Software Support
   - Billing
   - Feature Request
   - Other
2. Choose priority:
   - Low: General inquiries
   - Medium: Non-urgent issues
   - High: Important problems
   - Urgent: Critical issues
3. Enter subject and description
4. Submit ticket

### Ticket Number
Format: **TICK-YYYYMMDD-XXXX**
- Automatically generated
- Use for reference
- Unique identifier

## Managing Your Tickets

### View Tickets
- Go to **My Tickets**
- See all your tickets
- Filter by status

### Ticket Statuses
- **Open**: New ticket
- **In Progress**: Being worked on
- **Answered**: Response received
- **Closed**: Resolved

### Responding to Tickets
1. Open ticket
2. Type response
3. Submit reply
4. Wait for update

### Rating Closed Tickets
- Rate 1-5 stars
- Provide feedback
- Help improve service

## Auto-Close System
- Tickets auto-close after 7 days inactivity
- Warning sent 2 days before
- Respond to keep open

## Escalation
For urgent issues:
1. Set priority to Urgent
2. Include "URGENT" in subject
3. Provide contact number
4. Expect faster response`,
    relatedTopics: ['common-issues'],
    keywords: ['support', 'ticket', 'help', 'issue', 'problem'],
    contextPaths: ['/my-tickets']
  },

  // Payments and Invoicing
  {
    id: 'payments-invoicing',
    title: 'Payments & Invoicing',
    category: 'payments',
    description: 'Generate invoices and manage payments',
    content: `# Payments & Invoicing

## Pro-Forma Invoice

### When to Use
- Before payment received
- Quote to client
- Reservation stage

### Generate Pro-Forma
1. Go to **Payments** → **Generate Pro-Forma**
2. Select development and unit
3. Enter client details
4. Add line items
5. Generate PDF

## Tax Invoice

### When to Use
- After payment received
- Official tax document
- For accounting

### Generate Invoice
1. Go to **Payments** → **Generate Invoice**
2. Enter invoice details
3. Add VAT if applicable
4. Generate and send

## Agent Payments

### Pay HQ Agent
1. Select agent
2. Enter commission amount
3. Add reference
4. Process payment

### Pay Partner
1. Select partner
2. Calculate commission split
3. Enter payment details
4. Process payment

## Consultant Loans

### Issue Loan
1. Select consultant
2. Enter loan amount
3. Set interest rate (optional)
4. Record loan

### Record Payment
1. Select active loan
2. Enter payment amount
3. Add reference
4. Update balance

### View History
- Transaction history
- Interest calculations
- Outstanding balance
- Payment schedule`,
    relatedTopics: ['commission-settings', 'consultant-loans'],
    keywords: ['payment', 'invoice', 'proforma', 'commission', 'loan'],
    contextPaths: ['/payments'],
    windowIds: ['proforma', 'invoice', 'pay-agent', 'consultant-loans']
  },

  // Bonds Report
  {
    id: 'bonds-report',
    title: 'Bonds Report',
    category: 'reports',
    description: 'Generate and understand bonds financing reports',
    content: `# Bonds Report

## Purpose
Track all units that went through bond financing process.

## Report Sections

### Unit Information
- Development and unit details
- Purchase price and deposit
- Financing amount required

### Bond Status
- **Application Submitted**: Date submitted to bank
- **AIP (Approval in Principle)**: Initial approval
- **Final Grant**: Full approval received
- **Declined**: Application rejected

### Timeline Tracking
- Application date
- AIP date
- Grant date
- Transfer date

## Using Filters

### Date Range
Filter by:
- Application date
- AIP date
- Grant date

### Status Filter
- In Progress
- Approved
- Declined
- Transferred

### Bank Filter
- Select specific banks
- View approval rates

## Commission Calculations

### Bond Originator Fee
- 0.65% of bond amount (configurable)
- Paid by bank to originator
- Split with consultant if applicable

### Consultant Share
- Level 1: 30% of originator fee
- Level 2: 50% of originator fee
- Level 3: Not applicable

## Exporting Reports

### Summary View
- Total applications
- Approval rate
- Average processing time
- Commission totals

### Detailed Export
- Full transaction details
- Supporting documents
- Commission breakdowns`,
    relatedTopics: ['sales-reports', 'commission-reports'],
    keywords: ['bonds', 'finance', 'mortgage', 'bank', 'loan'],
    contextPaths: ['/reports/bonds'],
    windowIds: ['bonds-report']
  },

  // Cash Deals Report
  {
    id: 'cash-deals-report',
    title: 'Cash Deals Report',
    category: 'reports',
    description: 'Track and report on cash purchase transactions',
    content: `# Cash Deals Report

## Overview
Monitor all cash purchases without financing.

## Report Components

### Deal Information
- Unit details and pricing
- Buyer information
- Sales consultant details

### Payment Tracking
- Initial deposit
- Progress payments
- Final payment
- Total received

### Timeline
- Reservation date
- OTP date
- Payment milestones
- Transfer date

## Payment Schedule Types

### Structured Payments
Pre-defined payment plan:
- 10% deposit
- 30% on foundation
- 30% on roof
- 30% on completion

### Custom Payments
Negotiated payment terms:
- Flexible percentages
- Custom milestones
- Client-specific arrangements

## Filtering Options

### Development
- All developments
- Specific project
- Multiple selection

### Payment Status
- **Partial**: Payments in progress
- **Complete**: 100% paid
- **Overdue**: Missed payment dates

### Date Filters
- Deal date
- Payment dates
- Completion date

## Analytics

### Payment Performance
- Average days to full payment
- Payment adherence rate
- Early payment percentage

### Cash Flow Analysis
- Monthly cash receipts
- Projected income
- Outstanding amounts

## Export Options
- PDF summary report
- Excel with payment details
- Payment schedules`,
    relatedTopics: ['sales-reports', 'bonds-report'],
    keywords: ['cash', 'payment', 'purchase', 'deals'],
    contextPaths: ['/reports/cash-deals'],
    windowIds: ['cash-deals-report']
  },

  // Commission Reports
  {
    id: 'commission-reports',
    title: 'Commission Reports',
    category: 'reports',
    description: 'Generate commission calculations and payment reports',
    content: `# Commission Reports

## Report Types

### Individual Commission Report
Shows commissions for specific consultant:
- Deals closed
- Commission earned
- Payments received
- Outstanding balance

### Grouped Commission Report
Aggregated by:
- Development
- Time period
- Consultant level
- Partner organization

## Commission Breakdown

### Base Calculation
1. Sale price or fixed amount
2. Remove VAT (÷ 1.15)
3. Apply consultant percentage
4. Deduct referral if applicable

### Consultant Levels
- **Level 1 (30%)**: R43,478 × 30% = R13,043
- **Level 2 (50%)**: R43,478 × 50% = R21,739
- **Level 3 (70%)**: R43,478 × 70% = R30,435

### Referral Impact
- Referral gets 5% of de-vatted
- Reduces HQ portion only
- Consultant amount unchanged

## Payment Status

### Pending
- Deal complete
- Commission calculated
- Awaiting payment

### Partial
- Some payment received
- Balance outstanding

### Paid
- Full commission paid
- Payment date recorded

## Report Features

### Summary Section
- Total sales value
- Total commission earned
- Total paid
- Outstanding balance

### Detail Section
- Individual transactions
- Calculation breakdown
- Payment history
- Supporting documents

## Export Formats

### PDF Report
- Professional layout
- Company branding
- Digital signatures

### Excel Export
- Raw data
- Formulas included
- Pivot table ready

### CSV Export
- Simple format
- Import to accounting`,
    relatedTopics: ['commission-settings', 'sales-reports'],
    keywords: ['commission', 'payment', 'consultant', 'earnings'],
    contextPaths: ['/reports/commissions'],
    windowIds: ['commission-report', 'grouped-commission-report']
  },

  // Consultant Loans
  {
    id: 'consultant-loans',
    title: 'Consultant Loan Management',
    category: 'payments',
    description: 'Manage loans and advances to consultants',
    content: `# Consultant Loan Management

## Loan Types

### Commission Advance
- Advance against future commissions
- Automatic deduction from earnings
- No interest option available

### Personal Loan
- Separate from commissions
- Manual repayment required
- Interest rate applicable

## Creating a Loan

### Steps
1. Navigate to **Consultant Loans**
2. Select consultant
3. Enter loan details:
   - Amount
   - Interest rate (0% for advances)
   - Repayment terms
   - Start date
4. Add notes/reference
5. Create loan

### Approval Process
- Requires manager approval
- Password confirmation
- Logged in activity

## Repayment Methods

### Automatic Deduction
- Set percentage of commission
- Deducts until paid
- Minimum payment amounts

### Manual Payments
- Record individual payments
- Cash/EFT/card options
- Payment receipts

### Lump Sum
- Full settlement
- Early payment option
- Interest recalculation

## Interest Calculation

### Simple Interest
- Fixed rate on principal
- Monthly calculation
- Added to balance

### Compound Interest
- Interest on interest
- Configurable period
- Higher total cost

### Interest-Free
- Commission advances
- Short-term loans
- Special arrangements

## Reporting

### Individual Statement
- Loan details
- Payment history
- Current balance
- Interest accrued

### Portfolio Report
- All active loans
- Total exposure
- Payment performance
- Risk analysis

## Managing Defaults

### Grace Period
- 7 days standard
- Configurable per loan
- Warning notifications

### Collections Process
1. Automated reminders
2. Commission freeze
3. Manager intervention
4. Formal demand`,
    relatedTopics: ['payments-invoicing', 'commission-reports'],
    keywords: ['loan', 'advance', 'consultant', 'repayment', 'interest'],
    contextPaths: ['/consultant-loans'],
    windowIds: ['consultant-loans']
  },

  // Development Documents
  {
    id: 'development-documents',
    title: 'Managing Development Documents',
    category: 'developments',
    description: 'Upload and manage development documentation',
    content: `# Development Documents

## Required Documents

### SDP (Site Development Plan)
- Master layout plan
- Unit positioning
- Common areas
- Infrastructure

### Building Plans
- Architectural drawings
- Unit floor plans
- Elevations
- Specifications

### Marketing Material
- Brochures
- Price lists
- Feature sheets
- Virtual tours

## Document Upload

### File Requirements
- **PDF**: Recommended format
- **Images**: JPG, PNG (max 5MB)
- **Size Limit**: 20MB per file
- **Naming**: Use descriptive names

### Upload Process
1. Go to Development Details
2. Click **Documents** tab
3. Select document type
4. Choose file
5. Add description
6. Upload

## Document Management

### Version Control
- Keep latest version active
- Archive old versions
- Track upload dates
- Maintain history

### Access Control
- Public documents
- Agent-only documents
- Internal documents
- Restricted access

### Sharing Documents
- Generate shareable links
- Set expiry dates
- Track downloads
- Revoke access

## Using Documents

### In Sales Process
- Attach to quotes
- Include in proposals
- Send to clients
- Reference in deals

### For Marketing
- Website downloads
- Email attachments
- WhatsApp sharing
- Portal uploads

### Legal Compliance
- Keep signed copies
- Maintain approvals
- Store certificates
- Archive correspondence`,
    relatedTopics: ['create-development', 'edit-development'],
    keywords: ['documents', 'sdp', 'plans', 'upload', 'files'],
    contextPaths: ['/developments'],
    windowIds: ['development-documents']
  },

  // Activity Monitoring
  {
    id: 'activity-monitoring',
    title: 'Activity Log & Monitoring',
    category: 'settings',
    description: 'Track and monitor system activities',
    content: `# Activity Monitoring

## Activity Types Tracked

### User Actions
- Login/Logout
- Profile updates
- Password changes
- Permission changes

### Sales Activities
- Pre-reservations
- Reservations
- Deal captures
- Cancellations

### Document Activities
- Uploads
- Downloads
- Deletions
- Shares

### System Changes
- Settings updates
- User creation
- Development changes
- Status updates

## Viewing Activity Logs

### Access Levels
- **Super User**: All activities
- **Manager**: Department activities
- **User**: Own activities only

### Filtering Options
- Date range
- User
- Activity type
- Entity type
- Keywords

### Export Options
- PDF report
- Excel spreadsheet
- CSV file

## Understanding Log Entries

### Entry Components
- Timestamp
- User name and email
- Action performed
- Entity affected
- IP address
- Result status

### Action Types
- CREATE
- UPDATE
- DELETE
- VIEW
- EXPORT
- LOGIN
- LOGOUT

## Compliance & Auditing

### Data Retention
- 90 days standard
- Archived after period
- Permanent for critical

### Audit Trail
- Immutable records
- Tamper-proof
- Regulatory compliant
- Court admissible

### Privacy Considerations
- POPIA compliant
- User consent
- Data minimization
- Access restrictions`,
    relatedTopics: ['user-management', 'system-settings'],
    keywords: ['activity', 'log', 'audit', 'monitor', 'track'],
    contextPaths: ['/activity-log', '/activities'],
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER
    ]
  },

  // PropCloud HQ Navigation
  {
    id: 'propcloud-hq-navigation',
    title: 'Navigating PropCloud HQ',
    category: 'getting-started',
    description: 'Master the PropCloud HQ interface and window management',
    content: `# PropCloud HQ Navigation

## Interface Overview

### Menu Structure
- **Development**: Property management
- **Sales Tools**: Deal processes
- **CRM**: Lead management
- **Reports**: All reporting
- **Payments**: Financial tools
- **Admin Tools**: System management

### Window Management
- Multiple windows open simultaneously
- Drag to reposition
- Resize from corners
- Minimize to taskbar
- Close with X button

## Working with Windows

### Opening Windows
1. Click menu item
2. Window opens centered
3. Auto-focuses on first field

### Managing Multiple Windows
- Stack windows
- Side-by-side view
- Cascade arrangement
- Minimize unused

### Window Memory
- Position remembered
- Size retained
- State preserved
- Auto-restore

## Performance Tips

### Optimal Usage
- Close unused windows
- Minimize background windows
- Use search instead of browsing
- Keyboard navigation faster

### Troubleshooting
- Refresh if windows stuck
- Clear cache if slow
- Check internet connection
- Update browser`,
    relatedTopics: ['welcome', 'getting-started'],
    keywords: ['navigation', 'propcloud', 'hq', 'windows', 'interface'],
    contextPaths: ['/propcloud-hq'],
    windowIds: ['*']
  },

  // Training System
  {
    id: 'training-system',
    title: 'Training & Resources',
    category: 'getting-started',
    description: 'Access training materials and resources',
    content: `# Training & Resources

## Training Modules

### Getting Started
- System overview
- Navigation basics
- First login steps
- Profile setup

### Sales Training
- Deal capture process
- Pre-reservation workflow
- OTP procedures
- Commission structure

### CRM Training
- Lead management
- Contact creation
- Follow-up strategies
- Conversion techniques

### Reports Training
- Running reports
- Understanding data
- Export options
- Analysis tips

## Training Formats

### Video Tutorials
- Step-by-step guides
- Screen recordings
- Best practices
- Tips and tricks

### Documentation
- Written guides
- Process flowcharts
- Quick reference cards
- FAQs

### Live Sessions
- Weekly webinars
- Q&A sessions
- Feature updates
- Best practices

## Certification

### Basic Certification
- Complete all modules
- Pass assessment
- Receive certificate
- Badge on profile

### Advanced Certification
- Specialized tracks
- Role-specific training
- Expert level
- Annual renewal

## Resources

### Templates
- Email templates
- SMS templates
- Document templates
- Report templates

### Scripts
- Sales scripts
- Phone scripts
- Objection handling
- Closing techniques

### Tools
- Commission calculator
- Bond calculator
- ROI calculator
- Market analysis`,
    relatedTopics: ['welcome', 'getting-started'],
    keywords: ['training', 'learn', 'tutorial', 'video', 'certification'],
    contextPaths: ['/training']
  },

  // Cancel Deal Process
  {
    id: 'cancel-deal',
    title: 'Cancelling a Deal',
    category: 'sales',
    description: 'How to properly cancel a deal or reservation',
    content: `# Cancelling a Deal

## When to Cancel

### Client Initiated
- Buyer withdraws
- Finance declined
- Change of mind
- Found alternative

### Admin Initiated
- Non-payment
- Document issues
- Fraud detected
- Timer expired

## Cancellation Process

### Step 1: Access Deal
1. Navigate to unit
2. Open deal details
3. Click **Cancel Deal**

### Step 2: Select Reason
Choose from:
- **Buyer Withdrew**: Client decision
- **Finance Declined**: Bond rejected
- **Admin Cancel**: Internal decision
- **Timer Expired**: Automatic
- **Other**: Specify reason

### Step 3: Enter Details
- Detailed explanation
- Supporting documents
- Refund details (if applicable)
- Next steps

### Step 4: Confirm
- Review cancellation
- Enter password
- Confirm action
- Deal cancelled

## Post-Cancellation

### Unit Status
- Returns to **Available**
- Can be reserved again
- History preserved
- Notes retained

### Client Communication
- Automatic notification
- Cancellation letter
- Refund process
- Alternative options

### Commission Impact
- Reverses calculations
- Updates reports
- Adjusts balances
- Notifies consultant

## Refund Process

### Deposit Refunds
1. Calculate refund amount
2. Deduct penalties (if any)
3. Process refund
4. Update records

### Timeline
- 7-14 business days
- Bank processing time
- Confirmation sent
- Receipt provided

## Preventing Cancellations

### Best Practices
- Regular follow-ups
- Clear communication
- Manage expectations
- Address concerns early

### Warning Signs
- Delayed responses
- Payment issues
- Document delays
- Changed circumstances`,
    relatedTopics: ['capture-deal-workflow', 'unit-status'],
    keywords: ['cancel', 'cancellation', 'withdraw', 'refund'],
    contextPaths: ['/cancel-deal'],
    windowIds: ['cancel-deal']
  },

  // Facebook Lead Integration
  {
    id: 'facebook-leads',
    title: 'Facebook Lead Integration',
    category: 'crm',
    description: 'Managing leads from Facebook campaigns',
    content: `# Facebook Lead Integration

## Automatic Lead Capture

### Integration Setup
- Connected to Facebook Business
- Real-time webhook
- Automatic import
- Instant notifications

### Lead Information Captured
- Name and surname
- Email address
- Phone number
- Campaign source
- Ad details
- Timestamp

## Lead Processing

### Automatic Actions
1. Lead created in CRM
2. Score calculated
3. Assigned to consultant
4. Welcome message sent

### Lead Scoring Factors
- **Response time**: Faster = higher score
- **Campaign type**: Quality indicator
- **Completeness**: All fields filled
- **Engagement**: Form interactions

## Lead Management

### Bulk Operations
- Select multiple leads
- Assign to consultants
- Update status
- Add tags

### Assignment Rules
- **Round Robin**: Equal distribution
- **Performance**: Top performers first
- **Territory**: Location based
- **Manual**: Specific selection

## Lead Quality Analysis

### Metrics Tracked
- Cost per lead
- Conversion rate
- Response rate
- ROI by campaign

### Quality Indicators
- **High**: Complete info, quick response
- **Medium**: Partial info, delayed
- **Low**: Minimal info, no response

## Follow-up Automation

### Immediate Response
- Auto SMS within 1 minute
- Email within 5 minutes
- WhatsApp if enabled

### Nurture Sequence
- Day 1: Welcome
- Day 3: Information
- Day 7: Check-in
- Day 14: Offer

## Reporting

### Campaign Performance
- Leads by campaign
- Cost analysis
- Conversion tracking
- ROI calculation

### Consultant Performance
- Response times
- Conversion rates
- Follow-up compliance
- Lead outcomes`,
    relatedTopics: ['lead-management', 'crm-dashboard'],
    keywords: ['facebook', 'leads', 'campaign', 'marketing', 'social'],
    contextPaths: ['/crm'],
    windowIds: ['facebook-leads']
  },

  // Partner Management
  {
    id: 'partner-management',
    title: 'Partner Management',
    category: 'users',
    description: 'Managing partner organizations and agents',
    content: `# Partner Management

## Partner Structure

### Independent Partner Level 3
- Own company/brand
- 70% commission rate
- Can have agents
- Separate reporting

### Partner Agents
- Work under partner
- Commission split with partner
- Use partner branding
- Limited permissions

## Setting Up Partners

### Create Partner Account
1. Go to **Users** → **Create User**
2. Select **Independent Partner Level 3**
3. Enter partner details:
   - Company name
   - Registration number
   - VAT number
   - Banking details
4. Set commission structure

### Add Partner Agents
1. Create user account
2. Select **Partner Agent Level 3**
3. Link to partner organization
4. Set agent commission percentage

## Partner Permissions

### What Partners Can Do
- View own deals
- Access partner reports
- Manage own agents
- Update company info

### What Partners Cannot Do
- Access HQ data
- View other partners
- Change commission rates
- Delete deals

## Partner Reporting

### Available Reports
- Partner sales report
- Agent performance
- Commission statements
- Deal pipeline

### Report Access
- Real-time data
- Export capabilities
- Branded reports
- Agent breakdown

## Commission Structure

### Partner Commission
Base: 70% of de-vatted commission

### Agent Split
Partner sets agent percentage:
- Example: Agent gets 50%
- Partner keeps 50%
- Of the 70% total

### Calculation Example
\`\`\`
Sale: R1,000,000
Commission: 5% = R50,000
De-vatted: R43,478
Partner (70%): R30,435
Agent (50% of partner): R15,217
Partner keeps: R15,218
\`\`\``,
    relatedTopics: ['user-management', 'commission-settings'],
    keywords: ['partner', 'agent', 'organization', 'company'],
    contextPaths: ['/users', '/partner'],
    roles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.INDEPENDENT_PARTNER_LVL3
    ]
  },

  // Troubleshooting
  {
    id: 'common-issues',
    title: 'Common Issues & Solutions',
    category: 'troubleshooting',
    description: 'Solutions to frequently encountered problems',
    content: `# Common Issues & Solutions

## Login Problems

### Forgot Password
1. Click "Forgot Password" on login page
2. Enter your email address
3. Check email for reset link
4. Create new password

### Account Locked
- Too many failed attempts
- Contact administrator
- Wait 30 minutes

### Email Not Received
- Check spam folder
- Verify email address
- Contact support

## Deal Capture Issues

### Can't Find Unit
- Check development selection
- Verify unit status (must be Available)
- Refresh the page
- Clear filters

### Document Upload Fails
- Check file size (max 20MB)
- Use PDF format
- Ensure stable internet
- Try different browser

### Timer Expired
- Pre-reservation: 48 hours
- Reservation: 72 hours
- Contact admin for extension
- One extension maximum

### Locked Sections
- Click edit button
- Enter your password
- Check your permissions
- Contact manager if needed

## Report Problems

### No Data Showing
- Check filter settings
- Verify date range
- Ensure proper permissions
- Refresh the report

### PDF Export Fails
- Clear browser cache
- Try different browser
- Check popup blocker
- Disable ad blockers

### Excel Export Issues
- Enable popups
- Check download folder
- Try CSV format
- Clear browser cache

## Status Issues

### Status Not Updating
- All required fields filled?
- Documents uploaded?
- Previous step completed?
- Check timer status

### Wrong Status Showing
- Check deal capture progress
- Verify payment records
- Review status history
- Contact support

### Status Stuck
- Check for errors
- Verify permissions
- Review activity log
- Request manual update

## Performance Issues

### Slow Loading
- Clear browser cache
- Check internet speed
- Try incognito mode
- Update browser
- Disable extensions

### Features Not Working
- Hard refresh (Ctrl+F5)
- Clear cookies
- Update browser
- Check JavaScript enabled
- Try different browser

### Timeout Errors
- Check internet connection
- Reduce data range
- Export in smaller batches
- Contact support

## Window Management Issues

### Windows Not Opening
- Check popup blocker
- Allow popups for site
- Refresh PropCloud HQ
- Clear browser cache

### Windows Frozen
- Close and reopen window
- Refresh main page
- Check for JavaScript errors
- Report to support

## Data Issues

### Missing Data
- Check filters
- Verify permissions
- Refresh data
- Check date ranges

### Duplicate Entries
- Check for double-click
- Review activity log
- Contact support
- Don't create again

## Browser Compatibility

### Recommended Browsers
- Chrome (latest)
- Firefox (latest)
- Edge (Chromium)
- Safari 14+

### Not Supported
- Internet Explorer
- Old browser versions
- Mobile browsers (limited)

## Contact Support
If issues persist:
1. Note error messages
2. Take screenshots
3. Record steps to reproduce
4. Create support ticket
5. Include browser/OS details`,
    relatedTopics: ['support-tickets'],
    keywords: ['error', 'problem', 'issue', 'fix', 'troubleshoot', 'help', 'solution'],
    contextPaths: ['*'] // Available everywhere
  }
];

// Helper function to get contextual help
export const getContextualHelp = (path: string, windowId?: string): HelpTopic[] => {
  return helpTopics.filter(topic => {
    // Check if topic matches current path
    const pathMatch = topic.contextPaths?.some(contextPath => {
      if (contextPath === '*') return true;
      if (contextPath.includes(':')) {
        // Handle dynamic routes like /developments/:id
        const pattern = contextPath.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^${pattern}$`);
        return regex.test(path);
      }
      return path.startsWith(contextPath);
    });

    // Check if topic matches current window (for PropCloud HQ)
    const windowMatch = windowId && topic.windowIds?.includes(windowId);

    return pathMatch || windowMatch;
  });
};

// Helper function to search help topics
export const searchHelpTopics = (query: string): HelpTopic[] => {
  if (!query.trim()) return [];
  
  const lowerQuery = query.toLowerCase();
  
  return helpTopics.filter(topic => {
    const titleMatch = topic.title.toLowerCase().includes(lowerQuery);
    const descMatch = topic.description.toLowerCase().includes(lowerQuery);
    const contentMatch = topic.content.toLowerCase().includes(lowerQuery);
    const keywordMatch = topic.keywords.some(k => k.toLowerCase().includes(lowerQuery));
    
    return titleMatch || descMatch || contentMatch || keywordMatch;
  });
};

// Helper function to get topics by category
export const getTopicsByCategory = (categoryId: string): HelpTopic[] => {
  return helpTopics.filter(topic => topic.category === categoryId);
};

// Helper function to get related topics
export const getRelatedTopics = (topicId: string): HelpTopic[] => {
  const topic = helpTopics.find(t => t.id === topicId);
  if (!topic) return [];
  
  return topic.relatedTopics
    .map(id => helpTopics.find(t => t.id === id))
    .filter(Boolean) as HelpTopic[];
};